import ButtonBase from "@mui/material/ButtonBase";
import Grid from "components/primitives/Grid";
import useAthlete from "hooks/useAthlete";
import useUnitFormatter from "hooks/useUnitFormatter";
import AthleteName from "components/AthleteName";
import { useNavigate } from "react-router-dom";
import AnalyticsService from "services/AnalyticsService";
import { DateTime, ExerciseMetric, CircuitMetric, FireStoreMeasurement, ScoreMetadata } from "types";
import logEvent from "utils/logEvent";
import * as LeaderboardsComponents from "../Leaderboards.components";
import Tooltip from "components/Tooltip";
import { LeaderboardComparePeriod } from "features/Leaderboards/LeaderboardTables/LeaderboardEditable/LeaderboardEditable.types";
import { useMemo } from "react";
import moment from "moment";
import LeaderboardFormHelpers from "features/Leaderboards/helpers/LeaderboardFormHelpers";
import { useTranslation } from "react-i18next";
import usePreferences from "hooks/usePreferences";

export const LeaderboardTablePercentageRow = (props: {
  position: number | null;
  measurement: FireStoreMeasurement;
  comparativeMeasurement: FireStoreMeasurement;
  metric?: ExerciseMetric | CircuitMetric;
  scoreMetadata?: ScoreMetadata;
  metricField?: string;
  sortOrder?: 0 | 1;
  comparePeriodType: LeaderboardComparePeriod;
  startDateComparePeriod: DateTime | null;
}) => {
  const { position, measurement, comparativeMeasurement, metric, scoreMetadata, metricField, sortOrder } = props;
  const athlete = useAthlete(measurement.athleteId, true);
  const formatUnit = useUnitFormatter();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [preferences] = usePreferences();

  const clickableProps = athlete && {
    component: ButtonBase,
    onClick: () => {
      logEvent("HUB_LIVE_ATHLETE_PROFILE_CHART_EXERCISE", {
        [AnalyticsService.PROPERTIES.EXERCISE_ID]: measurement.exerciseId,
        [AnalyticsService.PROPERTIES.EVENT_CLICK_LOCATION]: "liveLeaderboard",
      });
      navigate(`/athlete-hub/${athlete.id}?chartType=LINE&metadataId=${measurement.exerciseId}`);
    },
  };

  // Get field and sortOrder from either metric or props
  const field = metricField || metric?.field || "score";
  const isDescending = sortOrder === 1 || metric?.sortType === 1;

  const percentageValue = useMemo(
    () =>
      LeaderboardFormHelpers.getPercentageComparisonForMeasurements(
        measurement[field],
        comparativeMeasurement[field],
        isDescending
      ) * 100,
    [comparativeMeasurement, measurement, field, isDescending]
  );

  // Format the measurement value
  const formattedMeasurementValue = useMemo(() => {
    const value = measurement[field];

    if (metric) {
      return formatUnit(value, metric);
    }

    if (scoreMetadata?.displayMetadata) {
      const displayMeta = preferences.imperialSystemEnabled
        ? scoreMetadata.displayMetadata.imperial
        : scoreMetadata.displayMetadata.metric;

      if (displayMeta?.decimals != null && typeof value === "number") {
        return value.toFixed(displayMeta.decimals);
      }
    }

    return value;
  }, [measurement, field, metric, scoreMetadata, formatUnit, preferences.imperialSystemEnabled]);

  // Format the comparative value
  const formattedComparativeValue = useMemo(() => {
    const value = comparativeMeasurement[field];

    if (metric) {
      return formatUnit(value, metric);
    }

    if (scoreMetadata?.displayMetadata) {
      const displayMeta = preferences.imperialSystemEnabled
        ? scoreMetadata.displayMetadata.imperial
        : scoreMetadata.displayMetadata.metric;

      if (displayMeta?.decimals != null && typeof value === "number") {
        return value.toFixed(displayMeta.decimals);
      }
    }

    return value;
  }, [comparativeMeasurement, field, metric, scoreMetadata, formatUnit, preferences.imperialSystemEnabled]);

  const tooltipText = useMemo(() => {
    const isCustomPeriod = props.comparePeriodType.value === "Custom";
    const isAllTime = props.comparePeriodType.value === "All";

    if (isAllTime) {
      return t("leaderboard.table_percentage_tooltip_text_All", {
        percentageValue: percentageValue.toFixed(0),
        comparativeValue: formattedComparativeValue,
        comparePeriodTypeLabel: t(props.comparePeriodType.label),
      });
    }

    if (isCustomPeriod) {
      return t("leaderboard.table_percentage_tooltip_text_Custom", {
        percentageValue: percentageValue.toFixed(0),
        comparativeValue: formattedComparativeValue,
        startDateComparePeriod: moment(props.startDateComparePeriod).format("MMM DD, YYYY"),
      });
    }

    return t("leaderboard.table_percentage_tooltip_text", {
      percentageValue: percentageValue.toFixed(0),
      comparativeValue: formattedComparativeValue,
      comparePeriodTypeLabel: t(props.comparePeriodType.label),
    });
  }, [
    t,
    formattedComparativeValue,
    percentageValue,
    props.comparePeriodType.label,
    props.comparePeriodType.value,
    props.startDateComparePeriod,
  ]);

  const is100Percent = percentageValue.toFixed(0) === "100";

  return (
    <LeaderboardsComponents.TableRowStyledBox {...clickableProps}>
      <Grid container alignItems="center" p={0} spacing="0px">
        <LeaderboardsComponents.PositionCellGrid>{position}</LeaderboardsComponents.PositionCellGrid>
        <Grid size="grow">
          <Grid container alignItems="center" spacing={0} wrap="nowrap">
            <Grid>
              <AthleteName athlete={athlete} />
            </Grid>
            {is100Percent && (
              <Grid mr={1} fontSize={"0.77em"}>
                🏆
              </Grid>
            )}
          </Grid>
        </Grid>
        <LeaderboardsComponents.MetricCellGrid mr={1} fontSize={"0.75em"} color="grey.800">
          {formattedMeasurementValue}
        </LeaderboardsComponents.MetricCellGrid>
        <Tooltip placement="right" arrow title={tooltipText}>
          <LeaderboardsComponents.MetricCellGrid minWidth={"40px"}>
            {percentageValue.toFixed(0)}%
          </LeaderboardsComponents.MetricCellGrid>
        </Tooltip>
      </Grid>
    </LeaderboardsComponents.TableRowStyledBox>
  );
};
